import React, { Fragment, useEffect, useRef, useState } from 'react';
import { NavItem, Container, Navbar, Nav } from 'reactstrap';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { uniqueId } from 'lodash';

import NavigationEntity from '../../../types/NavigationEntity';
import NavigationEntityItem from '../../navigation/NavigationEntityItem';
import ConfigStore from '../../../store/ConfigStore';
import { modelOf } from '../../../prop-types';
import CustomNavigationLinkLocationType from '../../../types/CustomNavigationLinkLocation';
import NavigationCustomEntityItem from '../NavigationCustomEntityItem';

const FADE_THRESHOLD = 22;

const NavigationSectionSlider = ({ configStore, items }) => {
  const navRef = useRef(null);
  const [fadeLeft, setFadeLeft] = useState(false);
  const [fadeRight, setFadeRight] = useState(true);

  useEffect(() => {
    const navEl = navRef.current;

    const updateFades = () => {
      const { scrollWidth, scrollLeft, clientWidth } = navEl;

      setFadeLeft(scrollLeft > FADE_THRESHOLD);
      setFadeRight(scrollLeft + clientWidth + FADE_THRESHOLD < scrollWidth);
    };

    if (navEl) {
      navEl.addEventListener('scroll', updateFades, true);
      // Initial check
      updateFades();

      return () => navEl.removeEventListener('scroll', updateFades, true);
    }
  }, []);

  const fadeStyle = (fadeLeft, fadeRight) => ({
    WebkitMaskImage: `linear-gradient(
      to right,
      ${fadeLeft ? 'transparent' : 'currentColor'} 0%,
      currentColor 20%,
      currentColor 80%,
      ${fadeRight ? 'transparent' : 'currentColor'} 100%
    )`,
    maskImage: `linear-gradient(
      to right,
      ${fadeLeft ? 'transparent' : 'currentColor'} 0%,
      currentColor 20%,
      currentColor 80%,
      ${fadeRight ? 'transparent' : 'currentColor'} 100%
    )`,
  });

  const getNavItems = () => {
    return items.map((item) =>
      <Fragment key={uniqueId(item.id)}>
        {/* Custom Nav Links ABOVE */}
        {getCustomNavLinks(item, CustomNavigationLinkLocationType.ABOVE)}
        {/* Main Nav Item */}
        <NavItem className={classNames('NavigationSectionSlider__item', item.className)}>
          {getNavigationElement(item)}
        </NavItem>
        {/* Custom Nav Links BELOW */}
        {getCustomNavLinks(item, CustomNavigationLinkLocationType.BELOW)}
      </Fragment>
    );
  };

  const getCustomNavLinks = (item, position) => {
    if (!item.customNavLinks) return null;
    return item.customNavLinks.map((link, index) => {
      if(link.location === position && link.parent_sibling_id === item.id) {
        return (
          <NavigationCustomEntityItem
            key={uniqueId(link.id)}
            customNavLink={link}
            index={index}
          />
        );
      }
      return null;
    });
  };

  const getNavigationElement = (item) => {
    return item.path ? (
      <NavigationEntityItem
        key={item.id}
        entity={item}
        className='NavigationSectionSlider__link'
      />
    ) : null ;
  };

  const navItems = getNavItems();

  return (
    <div
      ref={navRef}
      className="NavigationSectionSlider__fader"
      style={{
        overflow: "scroll",
        whiteSpace: "nowrap",
        ...fadeStyle(fadeLeft, fadeRight),
      }}
    >
      <Navbar
        className={classNames('NavigationSectionSlider')}
        expand="lg"
      >
        <Container
          className={classNames({
            NavigationSectionSlider__container: configStore.topNavigation.center,
          })}
        >
          <Nav
            navbar
            className="NavigationSectionSlider__nav"
          >
            {navItems}
          </Nav>
        </Container>
      </Navbar>
    </div>
  );
};

NavigationSectionSlider.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  items: PropTypes.arrayOf(NavigationEntity).isRequired,
};

export default inject('configStore')(observer(NavigationSectionSlider));
